import { normalizeStage } from '../data/dealStages.js';
import { computeEstimatorRow, estimatorPartSale, estimatorStrLabel, isEstimatorRowExcluded, num } from './interestEstimator.js';
import { isInvestorPlaced } from './investorPlacement.js';

export const ASSET_MANAGEMENT_STATUS = {
  HELD: 'held',
  WATCH: 'watch',
  SOLD: 'sold',
};

const CLOSED_STAGES = new Set(['Closed', 'Funded']);

const DATE_FIELDS = [
  'leaseDate',
  'lease_date',
  'oglDate',
  'ogl_date',
  'effectiveDate',
  'effective_date',
  'leaseEffectiveDate',
  'lease_effective_date',
  'activeLeaseDate',
  'active_lease_date',
];

const PRIMARY_TERM_FIELDS = [
  'primaryTerm',
  'primary_term',
  'leasePrimaryTerm',
  'lease_primary_term',
  'leaseTerm',
  'lease_term',
  'termYears',
  'term_years',
];

const EXPIRATION_FIELDS = [
  'expirationDate',
  'expiration_date',
  'leaseExpirationDate',
  'lease_expiration_date',
  'primaryTermExpiration',
  'primary_term_expiration',
  'leaseExpDate',
  'lease_exp_date',
];

export function buyerLooksLikeParaclete(buyer) {
  const haystack = [
    buyer?.company,
    buyer?.name,
    buyer?.email,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes('paraclete');
}

export function dealIsHeldByParaclete(deal, buyers) {
  // Buyers live on the rows now — the deal-level disposition buyer is
  // often stale after a buyer change. Paraclete holds the deal when
  // every assigned buyer (deal-level + active rows) is Paraclete.
  const rows = (Array.isArray(deal?.interestEstimates) ? deal.interestEstimates : [])
    .filter((row) => row && row.sold !== true && row.ignored !== true);
  // A row placed with an investor is NOT held — it was sold, and this
  // function only ever reasoned about buyers, so such a row was invisible
  // to it. An investor row carries no buyerId, so `.filter(Boolean)` below
  // dropped it and `every()` was left deciding on the Paraclete rows alone.
  //
  // On the Terry Gould deal that read as a pure self-purchase: one 160-acre
  // tract to Paraclete ($1,000 of margin) and one to Matt Sanchez ($10,000),
  // so 91% of the deal was an investor sale being booked as something
  // Paraclete kept. buildParacleteBuyerCommissionShares pays a buy-side
  // agent and a coordinator and nothing else, so the disposition manager
  // showed 0%, and the investor rep, all three partners and the Operating
  // Fund showed nothing at all — while Accounting dropped the deal out of
  // commissionable deals entirely.
  //
  // A deal placed ENTIRELY with an investor already returned false here,
  // because it has no buyer ids to collect. This makes the mixed case agree
  // with that instead of flipping to the opposite answer.
  if (rows.some((row) => String(row?.investorId || '').trim())) return false;
  const ids = new Set([
    deal?.dispositionBuyerId,
    ...rows.map((row) => row.buyerId),
  ].filter(Boolean));
  if (!ids.size) return false;
  return [...ids].every((id) => buyerLooksLikeParaclete((buyers || []).find((item) => item.id === id)));
}

export function buyerLabelForDeal(deal, buyers) {
  const buyer = (buyers || []).find((item) => item.id === deal?.dispositionBuyerId);
  if (!buyer) return deal?.dispositionBuyerId ? 'Unknown buyer' : '-';
  return buyer.company || buyer.name || 'Unknown buyer';
}

function readFirst(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return '';
}

export function parseAssetDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function formatAssetDate(value) {
  const date = parseAssetDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function parsePrimaryTermYears(value) {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value).trim().toLowerCase();
  const match = text.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const n = Number(match[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (/\bmonth/.test(text)) return n / 12;
  return n;
}

function addYears(date, years) {
  if (!date || !Number.isFinite(years)) return null;
  const wholeYears = Math.trunc(years);
  const months = Math.round((years - wholeYears) * 12);
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + wholeYears);
  if (months) next.setMonth(next.getMonth() + months);
  return next;
}

function daysUntil(value) {
  const date = parseAssetDate(value);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.ceil((date.getTime() - today.getTime()) / 86400000);
}

function leaseRisk(asset) {
  if (asset.sold) return { key: 'sold', label: 'Sold', color: '#1e40af', bg: '#eff6ff' };
  if (!asset.activeLease) return { key: 'unleased', label: 'Unleased / Needs Lease', color: '#dc2626', bg: '#fef2f2' };
  if (!asset.expirationDate) return { key: 'missing', label: 'Missing Expiration', color: '#d97706', bg: '#fffbeb' };
  if (asset.daysToExpiration < 0) return { key: 'expired', label: 'Expired', color: '#b91c1c', bg: '#fee2e2' };
  if (asset.daysToExpiration <= 180) return { key: 'urgent', label: `${asset.daysToExpiration} Days Left`, color: '#c2410c', bg: '#fff7ed' };
  if (asset.daysToExpiration <= 365) return { key: 'watch', label: `${asset.daysToExpiration} Days Left`, color: '#a16207', bg: '#fefce8' };
  return { key: 'healthy', label: 'Active', color: '#047857', bg: '#ecfdf5' };
}

function productionStatus(row, assetManagement) {
  if (assetManagement?.productionStatus) return assetManagement.productionStatus;
  if (row?.productionVerifiedAt || row?.production_verified_at) return 'Verified';
  if (row?.producingUnitId || row?.producing_unit_id || row?.unitName) return 'Needs Production Check';
  return 'Needs Review';
}

function cleanAssetManagement(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function makeAssetRows(deals, buyers, investorEntities) {
  const buyerById = new Map((buyers || []).map((buyer) => [buyer.id, buyer]));
  const entityById = new Map((investorEntities || []).map((entity) => [entity.id, entity]));
  const rows = [];

  for (const deal of deals || []) {
    const stage = normalizeStage(deal?.stage);
    const buyer = buyerById.get(deal?.dispositionBuyerId);
    // A pending resale moves the deal back to Under Contract, but the
    // rows stay firm-held assets until the sale completes.
    const resalePending = deal?.assetSale?.status === 'under_contract';
    // An investor deal is a HELD asset too. Paraclete and the investor
    // jointly own the entity that takes title, so the firm's book does
    // not end at deals whose buyer record happens to be named
    // "Paraclete" — that name check would silently drop every
    // jointly-owned entity out of assets under management, which is
    // the one place they most need to be.
    const investorHeld = isInvestorPlaced(deal);
    if (!CLOSED_STAGES.has(stage) && !resalePending) continue;
    // Who holds it decides whether the WHOLE row is an asset. It does not
    // decide whether the deal has one: a row can be sold on to an outside
    // buyer with a slice kept back, and that slice is Paraclete's however
    // the disposition buyer is named.
    //
    // Randy Perry Franks is the case. Bought 100%, sold 90% to Sarvis
    // Energy Partners, kept 10% — and because the gate asked "is the buyer
    // Paraclete" rather than "did we keep any of it", the deal was skipped
    // whole and the retained tenth was in no book anywhere.
    //
    // Generalised past that one deal: a part sale is a part sale whoever
    // the rest went to, so what is HELD is always the retained slice. Who
    // holds it is a separate question with a different answer — on an
    // investor-placed deal the remainder stays with the jointly-owned
    // entity, not with Paraclete alone, so "assume the rest is ours" is
    // right about the quantity and wrong about the name.
    const firmHeld = investorHeld || buyerLooksLikeParaclete(buyer);
    const entity = investorHeld ? entityById.get(deal.investorEntityId) : null;

    const estimateRows = Array.isArray(deal.interestEstimates) && deal.interestEstimates.length
      ? deal.interestEstimates
      : [];

    estimateRows.forEach((rawRow, index) => {
      const computed = computeEstimatorRow(rawRow);
      if (computed?.excluded || isEstimatorRowExcluded(rawRow)) return;
      // Only the slice that stayed is an asset.
      //
      // Keyed on whether somebody actually typed a soldPct, NOT on who the
      // buyer is. An absent soldPct means "sold all of it" to the
      // estimator — its documented fallback — and every row written before
      // part sales existed has none, so reading those as retaining nothing
      // would zero every asset already in the book.
      const partSale = estimatorPartSale(rawRow);
      const retainedFraction = partSale ? 1 - (computed?.soldFraction ?? 1) : 1;
      // Nothing kept and nobody holding the rest: not an asset.
      if (!firmHeld && !partSale) return;
      if (partSale && !(retainedFraction > 0)) return;
      const assetManagement = cleanAssetManagement(rawRow?.assetManagement);
      const sold = !!assetManagement.sold || assetManagement.status === ASSET_MANAGEMENT_STATUS.SOLD;
      const leaseDate = readFirst(rawRow, DATE_FIELDS);
      const primaryTerm = readFirst(rawRow, PRIMARY_TERM_FIELDS);
      const explicitExpiration = readFirst(rawRow, EXPIRATION_FIELDS);
      const derivedExpiration = explicitExpiration
        || addYears(parseAssetDate(leaseDate), parsePrimaryTermYears(primaryTerm))?.toISOString()
        || '';
      const salePrice = num(assetManagement.salePrice);
      // What the slice we still hold cost, and what it is worth.
      //
      // sellTotal is priced on the slice that LEFT — it is the money the
      // sale brought in — so it is the wrong number for what is still on
      // the shelf. The retained share is valued the way any unsold
      // inventory is: at the quoted rate, on the units still held.
      const costBasis = partSale
        ? (computed?.retainedBuyTotal || 0)
        : (computed?.buyTotal || 0);
      const bookValue = partSale
        ? (computed?.quotedSellRate || 0) * (computed?.retainedUnit || 0)
        : (computed?.sellTotal || 0);
      const asset = {
        id: `${deal.id}-${rawRow?.id || index}`,
        dealId: deal.id,
        rowId: rawRow?.id || null,
        rowIndex: index,
        ownerName: deal.ownerName || 'Unnamed Deal',
        contactId: deal.contactId || null,
        // Who holds it. On an investor deal that is the entity named on
        // the deed, not Paraclete — the asset is jointly owned, and a
        // column that said "Paraclete" would be wrong on the one fact
        // the deed settles.
        buyerName: investorHeld
          ? (entity?.legalName || 'Investor entity')
          // When the disposition buyer IS Paraclete, its own record is
          // the right name and keeps its legal form — a part sale does
          // not change who holds the rest.
          //
          // When the buyer is somebody else and a slice was kept back,
          // the rest went to them and this slice did not. Their name on
          // Paraclete's own inventory would be wrong on the one fact the
          // row is recording.
          : (firmHeld
            ? (buyer?.company || buyer?.name || 'Paraclete')
            : 'Paraclete'),
        investorId: investorHeld ? deal.investorId : null,
        investorEntityId: investorHeld ? deal.investorEntityId : null,
        paracleteOwnershipPct: investorHeld && entity?.paracleteOwnershipPct != null
          ? Number(entity.paracleteOwnershipPct) : null,
        // The investor's rep part-owns the entity too. Null when the
        // split is unset; 0 when it is set and the rep holds none —
        // which are different answers and should not read alike.
        repOwnershipPct: investorHeld && entity?.paracleteOwnershipPct != null
          ? Number(entity.repOwnershipPct || 0) : null,
        stage,
        closeDate: deal.sellClosingDate || deal.buyClosingDate || deal.closingDate || deal.updatedAt || deal.createdAt || '',
        tractLabel: estimatorStrLabel(rawRow),
        state: rawRow?.state || deal.state || '',
        county: rawRow?.county || deal.county || '',
        activeLease: !!(rawRow.activeLease || leaseDate || explicitExpiration),
        leaseDate,
        primaryTerm,
        expirationDate: derivedExpiration,
        expirationWasDerived: !explicitExpiration && !!derivedExpiration,
        productionStatus: productionStatus(rawRow, assetManagement),
        nma: partSale ? (computed?.retainedNma || 0) : (computed?.nma || 0),
        nra: partSale ? (computed?.retainedNra || 0) : (computed?.nra || 0),
        // How much of the row this asset is, so a part-held row reads as
        // a tenth rather than looking like a whole one that is worth less
        // than its neighbours.
        retainedFraction,
        partSale,
        leaseRoyalty: computed?.leaseRoyalty || 0,
        costBasis,
        bookValue,
        unrealized: bookValue - costBasis,
        salePrice,
        realizedGain: sold && salePrice > 0 ? salePrice - costBasis : 0,
        sold,
        assetStatus: sold ? ASSET_MANAGEMENT_STATUS.SOLD : assetManagement.status || ASSET_MANAGEMENT_STATUS.HELD,
        assetManagement,
      };
      asset.daysToExpiration = daysUntil(asset.expirationDate);
      asset.risk = leaseRisk(asset);
      rows.push(asset);
    });
  }

  return rows.sort((a, b) => {
    if (a.sold !== b.sold) return a.sold ? 1 : -1;
    const aDays = a.daysToExpiration ?? Number.POSITIVE_INFINITY;
    const bDays = b.daysToExpiration ?? Number.POSITIVE_INFINITY;
    if (a.risk.key === 'missing' && b.risk.key !== 'missing') return -1;
    if (b.risk.key === 'missing' && a.risk.key !== 'missing') return 1;
    if (aDays !== bDays) return aDays - bDays;
    return a.ownerName.localeCompare(b.ownerName);
  });
}

/**
 * The risk buckets the summary counts, as the filter understands them.
 *
 * summarizeAssetRows counts THREE keys as urgent — expired, urgent and
 * unleased — and two as active: healthy and watch. The filter matched a
 * single key, so a chip reading "5 need attention" filtered the table
 * down to 3 and a chip reading "8 healthy" showed 6. A number that does
 * not match what pressing it shows is worse than no number.
 *
 * The dropdown's single keys still work: anything not named here falls
 * through to an exact match.
 */
const RISK_GROUPS = Object.freeze({
  attention: ['expired', 'urgent', 'unleased'],
  healthy: ['healthy', 'watch'],
});

export function riskKeyMatches(filter, key) {
  if (!filter || filter === 'all') return true;
  const group = RISK_GROUPS[filter];
  return group ? group.includes(key) : key === filter;
}

export function summarizeAssetRows(assetRows) {
  const summary = {
    total: assetRows.length,
    held: 0,
    sold: 0,
    active: 0,
    urgent: 0,
    missing: 0,
    costBasis: 0,
    bookValue: 0,
    unrealized: 0,
    saleProceeds: 0,
    realizedGain: 0,
  };
  for (const asset of assetRows || []) {
    if (asset.sold) {
      summary.sold += 1;
      summary.saleProceeds += asset.salePrice || 0;
      summary.realizedGain += asset.realizedGain || 0;
      continue;
    }
    summary.held += 1;
    summary.costBasis += asset.costBasis || 0;
    summary.bookValue += asset.bookValue || 0;
    summary.unrealized += asset.unrealized || 0;
    if (asset.risk.key === 'healthy' || asset.risk.key === 'watch') summary.active += 1;
    if (['expired', 'urgent', 'unleased'].includes(asset.risk.key)) summary.urgent += 1;
    if (asset.risk.key === 'missing') summary.missing += 1;
  }
  return summary;
}

// ── Deal-level resale ────────────────────────────────────────────────────────
// Paraclete buys the deal at close (disposition buyer = Paraclete,
// stage Closed/Funded) and holds it as an asset; a resale hands every
// still-held interest row to an outside buyer in one step.
// POLICY: asset resales generate NO commission payouts — the only
// commission on a self-purchased deal is the buy-side payout
// materialized when the deal originally reached Funded. The rows are
// stamped commissionExempt so downstream accounting can enforce that.

export function heldAssetsForDeal(assetRows, dealId) {
  return (assetRows || []).filter((asset) => asset.dealId === dealId && !asset.sold);
}

// Split a deal-level sale price across the held rows pro-rata by book
// value (equal split when no row carries book value). The last row
// absorbs the rounding remainder so the shares always sum to the
// entered price.
export function allocateResalePrice(heldAssets = [], salePrice) {
  const total = num(salePrice);
  const totalBook = heldAssets.reduce((sum, asset) => sum + (asset.bookValue || 0), 0);
  const shares = new Map();
  let allocated = 0;
  heldAssets.forEach((asset, index) => {
    let share;
    if (index === heldAssets.length - 1) {
      share = Math.round((total - allocated) * 100) / 100;
    } else {
      const weight = totalBook > 0 ? (asset.bookValue || 0) / totalBook : 1 / heldAssets.length;
      share = Math.round(total * weight * 100) / 100;
      allocated += share;
    }
    shares.set(asset.id, Math.max(0, share));
  });
  return shares;
}

export function buildDealResaleRows(deal, heldAssets = [], sale = {}, editorLabel = 'AUM') {
  const rows = Array.isArray(deal?.interestEstimates) ? [...deal.interestEstimates] : [];
  const shares = allocateResalePrice(heldAssets, sale.salePrice);
  const stamp = new Date().toISOString();
  for (const asset of heldAssets) {
    if (!rows[asset.rowIndex]) continue;
    const current = cleanAssetManagement(rows[asset.rowIndex].assetManagement);
    rows[asset.rowIndex] = {
      ...rows[asset.rowIndex],
      assetManagement: {
        ...current,
        sold: true,
        status: ASSET_MANAGEMENT_STATUS.SOLD,
        soldDate: sale.soldDate || stamp.slice(0, 10),
        saleBuyer: sale.buyerLabel || current.saleBuyer || '',
        saleBuyerId: sale.buyerId || null,
        salePrice: String(shares.get(asset.id) ?? ''),
        saleNotes: sale.notes || current.saleNotes || '',
        resale: true,
        commissionExempt: true,
        updatedAt: stamp,
        updatedBy: editorLabel,
      },
    };
  }
  return rows;
}

export function replaceAssetManagementOnDeal(deal, asset, patch, editorLabel = 'AUM') {
  const rows = Array.isArray(deal?.interestEstimates) ? [...deal.interestEstimates] : [];
  if (!rows[asset.rowIndex]) return rows;
  const current = cleanAssetManagement(rows[asset.rowIndex].assetManagement);
  const next = {
    ...current,
    ...patch,
    updatedAt: new Date().toISOString(),
    updatedBy: editorLabel,
  };
  if (next.sold) next.status = ASSET_MANAGEMENT_STATUS.SOLD;
  rows[asset.rowIndex] = {
    ...rows[asset.rowIndex],
    assetManagement: next,
  };
  return rows;
}
